export const RECOLECCION_ESTADOS = {
  solicitada: {
    label: "Solicitada",
    style: "bg-yellow-500 text-white",
  },
  recibida: {
    label: "Recibida",
    style: "bg-primary text-white",
  },
  cancelada: {
    label: "Cancelada",
    style: "border border-gray-300 text-gray-400",
  },
};

export function getOrderCollections(pedido) {
  if (!pedido || !Array.isArray(pedido.recoleccion_pedidos)) {
    return [];
  }

  return pedido.recoleccion_pedidos
    .map((relation) => relation.recoleccion)
    .filter(Boolean);
}

export function getCurrentOrderCollection(pedido) {
  const collections = getOrderCollections(pedido);

  return (
    collections.find((collection) => collection.estado === "solicitada") ||
    collections.find((collection) => collection.estado === "recibida") ||
    collections.find((collection) => collection.estado === "cancelada") ||
    null
  );
}

export function hasReceivedCollection(pedido) {
  return getOrderCollections(pedido).some(
    (collection) => collection.estado === "recibida"
  );
}

export function hasActiveCollection(pedido) {
  return getOrderCollections(pedido).some((collection) =>
    ["solicitada", "recibida"].includes(collection.estado)
  );
}

export function getOrderDeliveryState(pedido) {
  if (!Array.isArray(pedido?.envios)) {
    const serverState = pedido?.delivery_state;
    return [
      "complete",
      "pending",
      "evidence_pending",
      "failed",
      "not_started",
    ].includes(serverState)
      ? serverState
      : "not_started";
  }

  const envios = pedido.envios.filter(
    (envio) => envio.status !== "cancelado"
  );
  const hasPending = envios.some((envio) => envio.status === "pendiente");
  const hasSuccess = envios.some((envio) => envio.status === "exitoso");
  const hasDocumentedSuccess = envios.some(
    (envio) =>
      envio.status === "exitoso" &&
      Array.isArray(envio.evidencia) &&
      envio.evidencia.length > 0
  );
  const hasFailed = envios.some((envio) => envio.status === "fallido");

  if (hasDocumentedSuccess && !hasPending) return "complete";
  if (hasPending) return "pending";
  if (hasSuccess) return "evidence_pending";
  if (hasFailed) return "failed";
  return "not_started";
}

export function hasSuccessfulDelivery(pedido) {
  return Array.isArray(pedido?.envios)
    ? pedido.envios.some(
        (envio) =>
          envio.status !== "cancelado" && envio.status === "exitoso"
      )
    : ["complete", "evidence_pending"].includes(
        getOrderDeliveryState(pedido)
      );
}

export function hasActiveDelivery(pedido) {
  return Array.isArray(pedido?.envios)
    ? pedido.envios.some((envio) => envio.status === "pendiente")
    : getOrderDeliveryState(pedido) === "pending";
}

export function hasNonCancelledDelivery(pedido) {
  return Array.isArray(pedido?.envios)
    ? pedido.envios.some((envio) => envio.status !== "cancelado")
    : getOrderDeliveryState(pedido) !== "not_started";
}

export function isDeliveryComplete(pedido) {
  return getOrderDeliveryState(pedido) === "complete";
}

export function canStartDelivery(pedido) {
  return ["not_started", "failed"].includes(getOrderDeliveryState(pedido));
}

export function canStartCollectionDelivery(pedido) {
  return (
    isStandaloneProviderOrder(pedido) &&
    hasReceivedCollection(pedido) &&
    canStartDelivery(pedido)
  );
}

export function canCancelAssignedOrder(pedido, actor) {
  const activeCollections = getOrderCollections(pedido).filter((candidate) =>
    ["solicitada", "recibida"].includes(candidate.estado)
  );

  if (activeCollections.length === 0) return false;
  if (actor === "admin") return true;

  return (
    actor === "provider" &&
    activeCollections.every((collection) => collection.estado === "solicitada")
  );
}

export function isStandaloneProviderOrder(pedido) {
  return pedido?.proveedor?.standalone === true;
}

export function isBillableDelivery(pedido, envio) {
  if (!pedido || !envio) return false;
  return pedido.proveedor?.standalone !== true || envio.recoleccion != null;
}

export function hasLockedProviderOperations(
  pedido,
  collectionStateLoaded = true
) {
  return (
    isStandaloneProviderOrder(pedido) &&
    (collectionStateLoaded !== true ||
      pedido.active_collection === true ||
      hasActiveCollection(pedido))
  );
}

export function isEligibleForCollection(pedido, proveedor) {
  if (!pedido || !proveedor || proveedor.standalone !== true) {
    return false;
  }

  if (
    pedido.proveedor &&
    String(pedido.proveedor.id) !== String(proveedor.id)
  ) {
    return false;
  }

  if (
    pedido.require_entrega !== true ||
    pedido.cancelado === true ||
    (pedido.require_aprobacion === true && pedido.aprobado !== true) ||
    !canStartDelivery(pedido)
  ) {
    return false;
  }

  return !getOrderCollections(pedido).some((collection) =>
    ["solicitada", "recibida"].includes(collection.estado)
  );
}

export function collectionStatus(estado) {
  return (
    RECOLECCION_ESTADOS[estado] || {
      label: estado || "Sin estado",
      style: "border border-gray-300 text-gray-400",
    }
  );
}

export function formatCollectionAddress(direccion) {
  if (!direccion) {
    return "Sin dirección";
  }

  const street = [direccion.direccion_calle, direccion.direccion_exterior]
    .filter(Boolean)
    .join(" ");
  const location = [
    direccion.direccion_colonia,
    direccion.direccion_codigo,
    direccion.direccion_estado,
  ]
    .filter(Boolean)
    .join(", ");

  return [street, location].filter(Boolean).join(", ") || "Sin dirección";
}
